import { defineStore } from 'pinia'
import { reactive, computed } from 'vue' // Import reactive
import dialService from '@/services/api/dial.service'
import callSocketService from '@/services/socket/callSocket.service'
import { getSocket } from '@/services/socket/websocket.service'
import { getOrderById } from '@/services/api/order.service'
import { usePaymentStore } from '@/stores/payment.store'
import { useAuthStore } from '@/stores/auth.store'

export const useCallStore = defineStore(
    'call',
    () => {
        // Define state using reactive
        const state = reactive({
            isCallInProgress: false,
            isDialInProgress: false,
            currentCallType: null, // 'voice' | 'video'
            callDetails: {
                orderId: null,
                callerId: null,
                receiverId: null,
                userName: null,
                userProfileImage: null,
                displayName: null,
            },
            isReceivingCall: false,
            incomingCallData: null,
            isInitialized: false, // To track if listeners are active
        })

        // Define initial state for reset
        const initialState = {
            isCallInProgress: false,
            isDialInProgress: false,
            currentCallType: null,
            callDetails: {
                orderId: null,
                callerId: null,
                receiverId: null,
                userName: null,
                userProfileImage: null,
                displayName: null,
            },
            isReceivingCall: false,
            incomingCallData: null,
            isInitialized: false,
            participantDisconnected: false,
            sessionData: null,
        }

        function normalizeDisplayName(...candidates) {
            for (const candidate of candidates) {
                if (candidate == null) continue
                const value = String(candidate).trim()
                if (value) return value
            }
            return null
        }

        // Define actions as functions
        async function initiateDial(targetUserId, callType) {
            if (state.isCallInProgress || state.isDialInProgress) {
                console.warn('Call or dial already in progress.')
                return
            }
            console.log(`Initiating ${callType} dial to ${targetUserId}`)
            state.isReceivingCall = false
            state.incomingCallData = null

            try {
                const responseData = await dialService.dialCreator(
                    targetUserId,
                    callType
                )

                // Response contains orderId and expiresAt (responseData is already response.data from dialService)
                console.log('Dial initiated response data:', responseData)
                if (
                    responseData &&
                    responseData.data &&
                    responseData.data.orderId
                ) {
                    // Now set dial in progress state as we have a valid orderId
                    state.isDialInProgress = true
                    state.currentCallType = callType
                    state.callDetails = {
                        ...state.callDetails,
                        receiverId: targetUserId,
                        orderId: responseData.data.orderId,
                    }
                    // Fetch order details to get recipient info
                    const orderDetails = await getOrderById(
                        responseData.data.orderId
                    )
                    if (orderDetails && orderDetails.creatorId) {
                        const creatorDisplayName = normalizeDisplayName(
                            orderDetails.creatorId.displayname,
                            orderDetails.creatorId.username
                        )
                        state.callDetails.userName =
                            creatorDisplayName
                        state.callDetails.userProfileImage =
                            orderDetails.creatorId.profileImage
                        state.callDetails.displayName = creatorDisplayName
                    }
                } else {
                    // This case should be less likely if dialService.dialCreator throws on non-success
                    // but good to have a fallback.
                    // If responseData.success is false, or no orderId, treat as error.
                    const errorMessage =
                        responseData?.message ||
                        'Failed to initiate call, no call ID returned or unsuccessful response.'
                    console.error(
                        'Dial initiation failed:',
                        errorMessage,
                        responseData
                    )
                    throw new Error(errorMessage)
                }
            } catch (error) {
                console.error('Error initiating call in store:', error)
                // state.isDialInProgress = false; // Ensure this is reset on any error
                // state.currentCallType = null;

                const shouldResetState = !(
                    error.response && error.response.status === 402
                )

                if (shouldResetState) {
                    resetCallState() // Resets general call state including isDialInProgress
                } else {
                    // For 402, we want to stop the dial in progress *if it was ever set*,
                    // and clear call type. resetCallState() is too broad here if modal is just shown.
                    state.isDialInProgress = false
                    state.currentCallType = null
                }

                if (error.response && error.response.data) {
                    const statusCode = error.response.status
                    let BEMessage =
                        error.response.data.message ||
                        'An unexpected error occurred.'

                    if (statusCode === 402) {
                        const paymentStore = usePaymentStore()
                        paymentStore.state.showBuyStarsModal = true // Open the modal
                    }
                    // Throw a new error with details for the component to handle
                    throw {
                        isCustomError: true,
                        statusCode: statusCode,
                        message: BEMessage,
                    }
                } else {
                    // This handles network errors, or errors thrown manually within the try block (like the new Error above)
                    // or if error.response is not available.
                    throw {
                        isCustomError: true,
                        statusCode: null, // No specific HTTP status code from a direct API response
                        message:
                            error.message || // Use the error's own message if available
                            'Network error or server is unreachable. Please try again.',
                    }
                }
            }
        }

        function handleIncomingDial(dialData) {
            console.log('Handling incoming dial:', dialData)
            if (state.isDialInProgress && !state.isReceivingCall) {
                console.warn(
                    'Already in an active dial, cannot handle new incoming dial for now.'
                )
                const socket = getSocket()
                if (socket && dialData.orderId) {
                    socket.emit('user_busy', {
                        orderId: dialData.orderId,
                    })
                }
                return
            }
            state.callDetails = {
                orderId: dialData.orderId,
                callerId: dialData.caller?.userId,
                userName: normalizeDisplayName(
                    dialData.clientDisplayName,
                    dialData.clientName,
                    dialData.clientUsername
                ),
                userProfileImage: dialData.clientPhoto,
                displayName: normalizeDisplayName(
                    dialData.clientDisplayName,
                    dialData.clientName,
                    dialData.clientUsername
                ),
            }
            state.currentCallType = dialData.callType
            state.isDialInProgress = false
            state.isReceivingCall = true
            state.incomingCallData = dialData
        }

        function handleCallRejected(dialData) {
            console.log('==== Handling call rejected')
            state.isDialInProgress = false
        }

        function handleCallCancelled(dialData) {
            console.log('==== Handling call cancelled')
            state.isReceivingCall = false
        }

        function handleCallReady(dialData) {
            console.log('==== Handling call ready')
            state.sessionData = dialData
            state.isCallInProgress = true
            state.isReceivingCall = false
            state.isDialInProgress = false
        }

        function handleParticipantDisconnected(dialData) {
            console.log('==== Handling participant disconnected')
            state.participantDisconnected = true
        }

        function handleParticipantReconnected(dialData) {
            console.log('==== Handling participant reconnected')
            state.participantDisconnected = false
        }

        // Handler for when the call was accepted on a different device/session
        function handleCallAcceptedElsewhere(dialData) {
            console.log('==== Call was accepted on another device', dialData)
            // Reset the receiving call state to dismiss the dial modal
            // without starting a call on this device
            state.isReceivingCall = false
            state.incomingCallData = null
            // Don't reset other state - the call might still be in progress on other device
        }

        function resetCallState() {
            console.log('Resetting call state')
            state.isCallInProgress = false
            state.isDialInProgress = false
            state.currentCallType = null
            state.callDetails = {
                orderId: null,
                callerId: null,
                receiverId: null,
                userName: null,
                userProfileImage: null,
                displayName: null,
            }
            state.isReceivingCall = false
            state.incomingCallData = null
            state.participantDisconnected = false
            state.sessionData = null
        }

        function initializeCallSockets() {
            if (
                callSocketService &&
                typeof callSocketService.setupCallSocketListeners === 'function'
            ) {
                const authStore = useAuthStore()
                // Pass the call store instance and the auth store instance
                callSocketService.setupCallSocketListeners(store, authStore)
            } else {
                console.error(
                    'callSocketService or setupCallSocketListeners is not available. Make sure callSocket.service.js is created and imported correctly.'
                )
            }
        }

        function cleanupCallSockets() {
            if (
                callSocketService &&
                typeof callSocketService.cleanupCallSockets === 'function'
            ) {
                callSocketService.cleanupCallSockets()
            }
        }

        function handleCallEnded(dialData) {
            console.log('==== Handling call ended', dialData)
            state.isCallInProgress = false
            // It's important to refresh balance upon call end.
            // The component (CallManager) is a better place for this,
            // as it can access the authStore to trigger the balance refresh.
            // However, if we get a call_ended event, we must reset the state.
            resetCallState()
        }

        function logoutClean() {
            console.log('Cleaning up call store for logout...')
            resetCallState() // Call directly
            cleanupCallSockets()
            console.log('Call store cleaned up.')
        }

        // Implementation of the $reset method required by Pinia
        function $reset() {
            console.log('Call store $reset called')
            // Reset state to initial values
            Object.keys(initialState).forEach((key) => {
                if (
                    typeof initialState[key] === 'object' &&
                    initialState[key] !== null
                ) {
                    // Deep copy for objects
                    state[key] = JSON.parse(JSON.stringify(initialState[key]))
                } else {
                    // Direct assignment for primitives
                    state[key] = initialState[key]
                }
            })
            // Also clean up any listeners
            cleanupCallSockets()
            console.log('Call store reset complete')
        }

        // Expose state and actions
        const store = {
            state,
            initiateDial,
            handleIncomingDial,
            handleCallRejected,
            handleCallCancelled,
            handleCallReady,
            handleCallEnded,
            handleCallAcceptedElsewhere,
            resetCallState,
            handleParticipantDisconnected,
            handleParticipantReconnected,
            initializeCallSockets,
            cleanupCallSockets,
            logoutClean,
            $reset,
        }

        return store
    },
    {
        persist: {
            storage: sessionStorage,
            // paths: [
            // ],
        },
    }
)
